import { useGetOffersQuery } from '../../api/lendingApi/lendingApiQueries';
import { TGetOffersResponse } from '../../types';
import logger from '../../utils/logger';

export type TSignupErrorType =
  | 'technical'
  | 'complete_personal_credit_check'
  | 'check_directors_match_companies_house';

export const useOffers = ({ enabled }: { enabled: boolean }) => {
  const { offersData, isLoadingOffers, refetchOffersData, isRefetchingOffers } =
    useGetOffersQuery({ enabled: enabled });

  // This should only be used for non-document data requirements.
  // Use getDocumentRequirements for document data requirements.
  const getDataRequirementActions = (
    offers?: TGetOffersResponse['data']['offers'],
  ) => {
    if (!offers) {
      return;
    }

    const dataRequirementActions: TGetOffersResponse['data']['offers'][number]['data_requirements'][number]['actions'] =
      [];

    offers.forEach((offer) => {
      offer.data_requirements?.forEach((requirement) => {
        if (requirement.category !== 'documents') {
          requirement.actions.forEach((action) => {
            dataRequirementActions.push(action);
          });
        }
      });
    });

    return dataRequirementActions;
  };

  const getErroredDataRequirements = (
    actions?: TGetOffersResponse['data']['offers'][number]['data_requirements'][number]['actions'],
  ) => {
    if (!actions) {
      return;
    }

    const errors: string[] = [];

    actions.forEach((action) => {
      if (action.status === 'error' && !action.is_optional) {
        errors.push(action.recommended_resolution_method);
      }
    });

    return errors;
  };

  const getSignupError = (offers?: TGetOffersResponse['data']['offers']) => {
    const errors = getErroredDataRequirements(
      getDataRequirementActions(offers),
    );

    if (errors && errors.length > 0) {
      const directorMismatchError = errors.find((action) => {
        return action === 'check_directors_match_companies_house';
      }) as TSignupErrorType | undefined;

      if (directorMismatchError) {
        return directorMismatchError;
      }

      const creditCheckError = errors.find((action) => {
        return action === 'complete_personal_credit_check';
      }) as TSignupErrorType | undefined;

      if (creditCheckError) {
        return creditCheckError;
      }

      return 'technical';
    }
  };

  const checkDocumentsRequired = (
    documentRequirements:
      | TGetOffersResponse['data']['offers'][number]['data_requirements'][number]['actions']
      | undefined,
    {
      allowSubmissionWithErrors = false,
    }: { allowSubmissionWithErrors?: boolean } = {},
  ): boolean => {
    if (!documentRequirements) {
      return false;
    }

    return Boolean(
      documentRequirements?.find(
        (action) =>
          // An example error state for a document is when it gets rejected after a review.
          (action.status === 'required' ||
            (!allowSubmissionWithErrors && action.status === 'error')) &&
          action.is_optional === false,
      ),
    );
  };

  const refetchOffers = async () => {
    try {
      const response = await refetchOffersData();

      const refetchedOffersData = response?.data
        ?.data as TGetOffersResponse['data'];

      return refetchedOffersData?.offers;
    } catch (error) {
      if (error instanceof Error) {
        logger.error('Failed to refetch offers', {
          error: error.message,
        });
      }
    }
  };

  const getSuggestedOffer = (
    offers: TGetOffersResponse['data']['offers'] | undefined,
  ) => {
    if (!offers) {
      return null;
    }

    // Hotfix: Issues of no suggested offer displays a blank Trading History page.
    // Logic should be moved to BE and ultimately source of issue needs to be found.
    // Started seeing same issues across TIP and signup from Jan 14th.
    return offers.find((offer) => offer.is_suggested) || offers[0];
  };

  const getDocumentRequirements = (
    offers?: TGetOffersResponse['data']['offers'],
  ) => {
    if (!offers) {
      return;
    }
    const suggestedOffer = getSuggestedOffer(offers);

    const suggestedOfferDocumentRequirements =
      suggestedOffer?.data_requirements?.find(
        (requirement) => requirement.category === 'documents',
      );

    if (suggestedOfferDocumentRequirements) {
      return suggestedOfferDocumentRequirements.actions;
    }

    for (const offer of offers) {
      const documentCategory = offer.data_requirements?.find(
        (requirement) => requirement.category === 'documents',
      );
      if (documentCategory) {
        return documentCategory.actions;
      }
    }
  };

  return {
    isLoadingOffers,
    getDataRequirementActions,
    getErroredDataRequirements,
    getSignupError,
    offers: offersData?.offers,
    getDocumentRequirements,
    isAutoEligible: Boolean(
      getSuggestedOffer(offersData?.offers)?.is_auto_eligible,
    ),
    checkDocumentsRequired,
    refetchOffers,
    isRefetchingOffers,
    offerStatus: offersData?.offer_creation_status,
  };
};
